import React, { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

import PageHeader from "../../components/PageHeader";
import { COLORS } from "../../constants";
import { optionsService } from "../../services/optionsService";
import {
  roleAdminService,
  type RoleAdminListItem,
} from "../../services/roleAdminService";
import RoleListCard, {
  emptyRoleForm,
  RolePermissionModal,
  type RoleFormState,
} from "./components/RolePermissionModal";

import RoleUsersModal from "./components/RoleUsersModal";

const queryKey = ["admin", "roles"] as const;

type ModalMode = "create" | "edit" | "clone";

function suggestCloneNome(nome: string, existingNames: string[]): string {
  const stripped = nome.replace(/\s*\(cópia(?:\s*\d+)?\)\s*$/i, "").trim();
  const taken = new Set(existingNames.map((n) => n.trim().toLowerCase()));
  let candidate = `${stripped} (cópia)`;
  let i = 2;
  while (taken.has(candidate.toLowerCase())) {
    candidate = `${stripped} (cópia ${i})`;
    i += 1;
  }
  return candidate;
}

function formFromRole(
  role: RoleAdminListItem,
  screenKeys: string[],
  nomeOverride?: string
): RoleFormState {
  return {
    nome: nomeOverride ?? role.nome,
    descricao: role.descricao ?? "",
    aplicaTodasLojas: role.aplicaTodasLojas,
    podeVerTodasLojas: role.podeVerTodasLojas,
    podePriorizarMontagem: role.podePriorizarMontagem,
    podeVerMaisInfoFichaProducao: role.podeVerMaisInfoFichaProducao,
    lojaIds: [...role.lojaIds],
    screenKeys: new Set(screenKeys),
  };
}

const ConfiguracaoAcessos: React.FC = () => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>("create");
  const [editingRole, setEditingRole] = useState<RoleAdminListItem | null>(
    null
  );
  const [usersModalRole, setUsersModalRole] =
    useState<RoleAdminListItem | null>(null);
  const [form, setForm] = useState<RoleFormState>(emptyRoleForm());

  const { data: roles = [], isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => roleAdminService.list(),
  });

  const { data: lojas = [] } = useQuery<Array<{ id: number; nome: string }>>({
    queryKey: ["lojas", "options"],
    queryFn: () => optionsService.getLojas(),
  });

  const lojasById = useMemo(
    () => new Map(lojas.map((l) => [l.id, l.nome])),
    [lojas]
  );

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setModalMode("create");
    setEditingRole(null);
    setForm(emptyRoleForm());
  }, []);

  const openCreate = () => {
    setModalMode("create");
    setEditingRole(null);
    setForm(emptyRoleForm());
    setIsModalOpen(true);
  };

  const openEdit = async (role: RoleAdminListItem) => {
    try {
      const detail = await roleAdminService.getById(role.id);
      setModalMode("edit");
      setEditingRole(role);
      setForm(formFromRole(role, detail.screenKeys));
      setIsModalOpen(true);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Não foi possível carregar o perfil.";
      toast.error(message);
    }
  };

  const openClone = async (role: RoleAdminListItem) => {
    try {
      const detail = await roleAdminService.getById(role.id);
      const nome = suggestCloneNome(
        role.nome,
        roles.map((r) => r.nome)
      );
      setModalMode("clone");
      setEditingRole(null);
      setForm(formFromRole(role, detail.screenKeys, nome));
      setIsModalOpen(true);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Não foi possível carregar o perfil para clonar.";
      toast.error(message);
    }
  };

  const openViewUsers = (role: RoleAdminListItem) => {
    setUsersModalRole(role);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        nome: form.nome.trim(),
        descricao: form.descricao.trim() || undefined,
        aplicaTodasLojas: form.aplicaTodasLojas,
        podeVerTodasLojas: form.podeVerTodasLojas,
        podePriorizarMontagem: form.podePriorizarMontagem,
        podeVerMaisInfoFichaProducao: form.podeVerMaisInfoFichaProducao,
        lojaIds: form.aplicaTodasLojas ? [] : form.lojaIds,
        screenKeys: Array.from(form.screenKeys),
      };

      if (!payload.nome) {
        throw new Error("Informe o nome do perfil.");
      }
      if (!payload.aplicaTodasLojas && payload.lojaIds.length === 0) {
        throw new Error("Selecione ao menos uma loja ou marque todas as lojas.");
      }

      if (modalMode === "edit" && editingRole) {
        return roleAdminService.update(editingRole.id, payload);
      }
      return roleAdminService.create(payload);
    },
    onSuccess: async () => {
      toast.success(
        modalMode === "edit"
          ? "Perfil atualizado."
          : modalMode === "clone"
            ? "Perfil clonado."
            : "Perfil criado."
      );
      await queryClient.invalidateQueries({ queryKey });
      closeModal();
    },
    onError: (error: Error) => {
      toast.error(error.message || "Não foi possível salvar o perfil.");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => roleAdminService.delete(id),
    onSuccess: async () => {
      toast.success("Perfil excluído.");
      await queryClient.invalidateQueries({ queryKey });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Não foi possível excluir o perfil.");
    },
  });

  const handleDelete = async (role: RoleAdminListItem) => {
    const result = await Swal.fire({
      title: "Excluir perfil?",
      text: `Deseja remover "${role.nome}"? Esta ação não pode ser desfeita.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: COLORS.PRIMARY,
      cancelButtonColor: COLORS.NEUTRAL_500,
      confirmButtonText: "Sim, excluir",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;
    deleteMutation.mutate(role.id);
  };

  if (isLoading) {
    return (
      <main className="mx-auto max-w-[1600px] px-4 py-8">
        <p className="text-neutral-600">Carregando…</p>
      </main>
    );
  }

  if (isError) {
    return (
      <main className="mx-auto max-w-[1600px] px-4 py-8">
        <p className="text-red-600">Erro ao carregar os perfis.</p>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-[1600px] px-4 py-5 sm:px-6 lg:px-8">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <PageHeader
          title="Permissões"
          description="Perfis e módulos liberados por loja"
        />
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-2 self-start rounded-lg px-4 py-2 text-sm font-semibold text-white shadow"
          style={{ backgroundColor: COLORS.PRIMARY }}
        >
          <Plus size={18} />
          Nova
        </button>
      </div>

      <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {roles.map((role) => (
          <RoleListCard
            key={role.id}
            role={role}
            lojasById={lojasById}
            onEdit={() => openEdit(role)}
            onClone={() => openClone(role)}
            onViewUsers={() => openViewUsers(role)}
            onDelete={() => handleDelete(role)}
          />
        ))}

        {roles.length === 0 && (
          <p className="col-span-full py-12 text-center text-neutral-500">
            Nenhum perfil cadastrado.
          </p>
        )}
      </div>

      <RolePermissionModal
        isOpen={isModalOpen}
        mode={modalMode}
        editingRole={editingRole}
        lojas={lojas}
        form={form}
        isSaving={saveMutation.isPending}
        onClose={closeModal}
        onChange={setForm}
        onSubmit={() => saveMutation.mutate()}
      />

      <RoleUsersModal
        role={usersModalRole}
        onClose={() => setUsersModalRole(null)}
      />
    </main>
  );
};

export default ConfiguracaoAcessos;
